import { useState, useEffect } from "react";
import {
  DropDownList,
  type DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  MaskedTextBox,
  type MaskedTextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { gxlookupTypeApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import type {
  LookupTypePayload,
  GetLookupTypeResponse,
} from "@/app/types/adminMenu";
import { useTranslation } from "react-i18next";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface IDProofOption {
  id: number;    // gxLookupId
  label: string; // gxLookupCode  (used for display AND config lookup)
}

interface IDProofTagProps {
  idType: IDProofOption | null;
  idNumber: string;
  countryDialCode: string;
  onIdTypeChange?: (value: IDProofOption) => void;
  onIdNumberChange?: (value: string) => void;
  onValidate?: (isValid: boolean) => void;
  triggerValidation?: boolean;
  disable?: boolean;
  // External error (e.g. "document already exists" from the contact-validate
  // API in AdminEditProfile). Same pattern as EmailInput's `mobileError` /
  // Mobilefiled's `mobileError` — merged with local format/required errors,
  // external error wins when both are present.
  validationMessage?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-document-type format + validation rules  (keyed by gxLookupCode)
// ─────────────────────────────────────────────────────────────────────────────

interface ProofConfig {
  placeholder: string;
  maxLength: number; // max characters AFTER formatting
  /** Value to seed the field with as soon as this ID type is selected. */
  initialValue?: string;
  /**
   * Kendo MaskedTextBox mask pattern. "0" = required digit, "9" = optional
   * digit — every other character (including plain digits like 7/8/4) is
   * rendered as a literal. Leave undefined for free-form fields.
   */
  mask?: string;
  format: (raw: string) => string;
  validate: (value: string, t: (k: string) => string) => string; // "" = valid
}

const proofConfigs: Record<string, ProofConfig> = {
  // ── Emirates ID ─────────────────────────────────────────────────────────────
  // Format : 784-YYYY-NNNNNNN-C  (15 digits displayed with dashes = 18 chars)
  "Emirates ID": {
    placeholder: "784-YYYY-NNNNNNN-C",
    maxLength: 18,
    // Emirates IDs always start with 784, so pre-fill it and lock it in as
    // a literal mask segment — the rest of the box shows as placeholder blanks.
    initialValue: "784-",
    mask: "784-0000-0000000-0",
    format(raw) {
      const digits = raw.replace(/\D/g, "").slice(0, 15);
      const p1 = digits.slice(0, 3);
      const p2 = digits.slice(3, 7);
      const p3 = digits.slice(7, 14);
      const p4 = digits.slice(14, 15);
      return [p1, p2, p3, p4].filter(Boolean).join("-");
    },
    validate(value, t) {
      const digits = value.replace(/\D/g, "");
      if (!digits)
        return t("please_enter_id_number") || "Please enter an ID number.";
      if (!digits.startsWith("784"))
        return (
          t("emirates_id_must_start_784") || "Emirates ID must start with 784."
        );
      if (digits.length !== 15)
        return (
          t("emirates_id_format_error") ||
          "Emirates ID must be 15 digits (784-YYYY-NNNNNNN-C)."
        );
      return "";
    },
  },

  // ── GCC National ID ──────────────────────────────────────────────────────────
  // Format : 10–15 continuous digits, no separators
  "GCC National ID": {
    placeholder: "Enter 10–15 digit national ID",
    maxLength: 15,
    format(raw) {
      return raw.replace(/\D/g, "").slice(0, 15);
    },
    validate(value, t) {
      const digits = value.replace(/\D/g, "");
      if (!digits)
        return t("please_enter_id_number") || "Please enter an ID number.";
      if (digits.length < 10 || digits.length > 15)
        return (
          t("gcc_id_length_error") ||
          "GCC National ID must be between 10 and 15 digits."
        );
      return "";
    },
  },

  // ── UAE Passport ─────────────────────────────────────────────────────────────
  // Format : 1 letter + 7 digits  e.g. A1234567
  "UAE Passport": {
    placeholder: "A1234567",
    maxLength: 8,
    format(raw) {
      return raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 8);
    },
    validate(value, t) {
      if (!value)
        return t("please_enter_id_number") || "Please enter an ID number.";
      if (!/^[A-Z][0-9]{7}$/.test(value))
        return (
          t("uae_passport_format_error") ||
          "UAE Passport must be 1 letter followed by 7 digits (e.g. A1234567)."
        );
      return "";
    },
  },

  // ── Passport ─────────────────────────────────────────────────────────────────
  // Format : Alphanumeric, uppercase, min 6 characters, max 9 characters, no spaces or symbols
  "Passport": {
    placeholder: "AB123456",
    maxLength: 9,
    format(raw) {
      return raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 9);
    },
    validate(value, t) {
      if (!value)
        return t("please_enter_id_number") || "Please enter an ID number.";
      if (!/^[A-Z0-9]{6,9}$/.test(value))
        return (
          t("passport_format_error") ||
          "Passport must be 6-9 alphanumeric characters (uppercase, no spaces or symbols)."
        );
      return "";
    },
  },
};

/** Fallback for any future lookup code not yet mapped above. */
const fallbackConfig: ProofConfig = {
  placeholder: "Enter ID number",
  maxLength: 20,
  format(raw) {
    return raw.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 20);
  },
  validate(value, t) {
    if (!value || value.trim().length < 5)
      return (
        t("please_enter_valid_id") ||
        "Please enter a valid ID number (min 5 characters)."
      );
    return "";
  },
};

const getConfig = (label: string): ProofConfig =>
  proofConfigs[label] ?? fallbackConfig;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const DynamicIDProofTag: React.FC<IDProofTagProps> = ({
  idType,
  idNumber,
  countryDialCode,
  onIdTypeChange,
  onIdNumberChange,
  onValidate,
  triggerValidation,
  disable = false,
  validationMessage: externalValidationMessage,
}) => {
  const { t } = useTranslation();

  const [proofList, setProofList] = useState<IDProofOption[]>([]);
  const [touched, setTouched] = useState(false);
  const [localValidationMessage, setLocalValidationMessage] = useState("");

  // ── Fetch ID proof types from API ──────────────────────────────────────────

  useEffect(() => {
    const fetchLookupTypes = async () => {
      try {
        const payload: LookupTypePayload = {
          gxLookupId: 0,
          gxLookupType: "SME_IDENTITY",
          gxLookupCode: "string",
          gxLookupValue: "string",
          gxLookupDesc: "string",
          gxLookupSeq: 0,
        };
        const response = await gxlookupTypeApi(payload);
        const formatted: IDProofOption[] = response.data.map(
          (item: GetLookupTypeResponse) => ({
            id: item.gxLookupId,
            label: item.gxLookupCode, // doubles as config key
          }),
        );
        setProofList(formatted);
      } catch (err) {
        console.error("Error fetching lookup types:", err);
      }
    };

    fetchLookupTypes();
  }, []);

  // Auto-select first item once list loads, seeding its initial value (e.g. "784" for Emirates ID)
  useEffect(() => {
    if (proofList.length > 0 && !idType) {
      const first = proofList[0];
      onIdTypeChange?.(first);
      onIdNumberChange?.(getConfig(first.label).initialValue ?? "");
    }
  }, [proofList]);

  // Reset validation when country changes
  useEffect(() => {
    setTouched(false);
    setLocalValidationMessage("");
  }, [countryDialCode]);

  // External validation trigger (form submit) — also fires when the parent
  // flips triggerValidation because an externalValidationMessage (API error)
  // just arrived, so the red border/message shows immediately.
  useEffect(() => {
    if (triggerValidation) {
      const msg = runValidation(idNumber);
      setLocalValidationMessage(msg);
      onValidate?.(!msg);
      if (msg) setTouched(true); // only show red border when there is an error
    } else {
      // triggerValidation cleared → form reset
      setTouched(false);
      setLocalValidationMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerValidation]);

  // Re-validate when idType changes while field is already touched
  useEffect(() => {
    if (touched && idNumber) {
      const msg = runValidation(idNumber);
      setLocalValidationMessage(msg);
      onValidate?.(!msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idType]);

  // External error (e.g. "ID already exists") should surface the red state
  // the same way EmailInput/Mobilefiled treat their `mobileError` prop.
  useEffect(() => {
    if (externalValidationMessage) {
      setTouched(true);
    }
  }, [externalValidationMessage]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Pure — returns error string or "" when valid. Does NOT set state. */
  const runValidation = (value: string): string => {
    if (!idType)
      return t("please_select_id_type") || "Please select an ID type.";
    return getConfig(idType.label).validate(value, t);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleProofChange = (e: DropDownListChangeEvent) => {
    const newType = e.value as IDProofOption;
    onIdTypeChange?.(newType);
    // Seed the field with the type's initial value (e.g. "784" for Emirates ID)
    onIdNumberChange?.(getConfig(newType.label).initialValue ?? "");
    setTouched(false);
    setLocalValidationMessage("");
  };

  const handleTextChange = (e: MaskedTextBoxChangeEvent) => {
    const raw = e.value ?? "";
    const config = idType ? getConfig(idType.label) : fallbackConfig;
    const formatted = config.format(raw);
    onIdNumberChange?.(formatted);

    if (touched) {
      const msg = runValidation(formatted);
      setLocalValidationMessage(msg);
      onValidate?.(!msg);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    const msg = runValidation(idNumber);
    setLocalValidationMessage(msg);
    onValidate?.(!msg);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const activeConfig = idType ? getConfig(idType.label) : fallbackConfig;
  // External (API) error takes priority over local format/required errors,
  // same precedence as EmailInput/Mobilefiled.
  const displayError = externalValidationMessage || localValidationMessage;
  const showError = touched && !!displayError;

  return (
    <div className="gx-dropdown relative">
      <div className={`gx_aadhar px-3 ${showError ? "invalid" : "valid"}`}>
        {/* ── Document-type dropdown ── */}
        <div className="w-30  relative">
          <DropDownList
            data={proofList}
            value={idType}
            onChange={handleProofChange}
            textField="label"
            dataItemKey="id"
            defaultItem={{ id: 0, label: t("Select ID Proof") }}
            disabled={disable}
          />
          {/* <span className="absolute top-1 gx_input_star gx_input_star_normal">
            *
          </span> */}
          <img
            src={`${ICON_BASE_PATH}down_arrow1.svg`}
            className="gx-mail-arrow absolute -right-1.5 top-4 ms-1"
            alt="mail"
          />
        </div>

        {/* ── Divider ── */}
        <div
          className="ms-[10px]"
          style={{ width: 1, height: 35, backgroundColor: "#E0E0E0" }}
        />

        {/* ── ID number input ── */}
        <div style={{ flex: 1 }}>
          <MaskedTextBox
            value={idNumber}
            onChange={handleTextChange}
            onBlur={handleBlur}
            mask={activeConfig.mask}
            placeholder={activeConfig.placeholder.toLowerCase()}
            className="k-input_phone"
            disabled={disable || !idType}
            style={{
              border: "none",
              outline: "none",
              fontSize: 14,
              fontWeight: 500,
              width: "100%",
              background: "transparent",
            }}
          />
        </div>
      </div>

      {/* ── Validation message ── */}
      {showError && (
        <p className="gx_input_validationmessage gx-font-regular">
          {displayError}
        </p>
      )}
    </div>
  );
};

export default DynamicIDProofTag;
